import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Book as BookIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  MenuBook as MenuBookIcon,
  AutoStories as ReadingIcon,
  AccessTime as TimeIcon,
  PlayArrow,
  Refresh,
  CheckCircle,
  Stop,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { adminAPI, readingSessionAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import CustomContainer from "../../components/common/CustomContainer";

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.dark` }}>
            {icon}
          </Avatar>
        </Grid>
        <Grid item xs>
          <Typography color="textSecondary" variant="subtitle2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);
const RecentActivityItem = ({ activity }) => {
  const { t } = useTranslation();

  if (!activity) {
    return null;
  }

  const getIcon = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case "started":
        return <PlayArrow />;
      case "resumed":
        return <Refresh />;
      case "completed":
        return <CheckCircle />;
      case "abandoned":
        return <Stop />;
      default:
        return <MenuBookIcon />;
    }
  };

  try {
    return (
      <ListItem alignItems="flex-start">
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: activity.iconColor || "grey.500" }}>
            {getIcon(activity.action_type)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={activity.title || t("admin.recentActivity.unknownActivity")}
          secondary={
            <>
              <Typography component="span" variant="body2" color="textPrimary">
                {activity.description ||
                  t("admin.recentActivity.noDescription")}
              </Typography>
              {activity.time ? ` — ${activity.time}` : ""}
            </>
          }
        />
      </ListItem>
    );
  } catch (error) {
    console.error("Error rendering activity item:", error);
    return (
      <ListItem>
        <ListItemText primary={t("common.errors.displayError")} />
      </ListItem>
    );
  }
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalUsers: 0,
    totalCategories: 0,
    activeReaders: 0,
    totalReadingTime: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats and activities separately for better error isolation
      const statsPromise = adminAPI.getDashboardStats().catch((error) => {
        console.error("Stats fetch error:", error);
        return { data: null };
      });

      const activitiesPromise = adminAPI.getRecentActivity().catch((error) => {
        console.error("Activities fetch error:", error);
        return { data: [] };
      });

      const [statsResponse, activitiesResponse] = await Promise.all([
        statsPromise,
        activitiesPromise,
      ]);

      if (statsResponse.data) {
        setStats(statsResponse.data);
      }

      if (Array.isArray(activitiesResponse.data)) {
        setRecentActivities(activitiesResponse.data);
      } else {
        console.error(
          "Unexpected activities response format:",
          activitiesResponse.data
        );
      }
    } catch (error) {
      console.error("Dashboard data fetch error:", error);
      setError(error);
      showSnackbar(
        error.response?.data?.message || t("dashboard.errors.loadFailed"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const quickActions = [
    {
      title: t("admin.actions.addBook"),
      icon: <MenuBookIcon />,
      action: () => navigate("/admin/books_management"),
      color: "primary",
    },
    {
      title: t("admin.actions.addCategory"),
      icon: <CategoryIcon />,
      action: () => navigate("/admin/categories"),
      color: "secondary",
    },
    {
      title: t("admin.actions.manageUsers"),
      icon: <PersonIcon />,
      action: () => navigate("/admin/users"),
      color: "success",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {t("admin.errorLoading")}
        </Typography>
        <Button variant="contained" onClick={fetchDashboardData}>
          {t("admin.retryLoading")}
        </Button>
      </Box>
    );
  }

  return (
    <CustomContainer>
      <Typography variant="h4" gutterBottom>
        {t("navigation.adminDashboard")}
      </Typography>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("dashboard.stats.totalBooks")}
            value={stats.totalBooks}
            icon={<BookIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("dashboard.stats.totalUsers")}
            value={stats.totalUsers}
            icon={<PersonIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("dashboard.stats.categories")}
            value={stats.totalCategories}
            icon={<CategoryIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("dashboard.stats.activeReaders")}
            value={stats.activeReaders}
            icon={<ReadingIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("dashboard.stats.totalReadingTime")}
            value={readingSessionAPI.formatDuration(stats.totalReadingTime)}
            icon={<TimeIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h6" gutterBottom>
        {t("admin.quickActions")}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Button
              fullWidth
              variant="outlined"
              color={action.color}
              startIcon={action.icon}
              onClick={action.action}
              sx={{ py: 2 }}
            >
              {action.title}
            </Button>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      <Paper sx={{ mt: 3 }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {t("admin.recentActivity.title")}
          </Typography>
          <IconButton
            onClick={fetchDashboardData}
            size="small"
            title={t("admin.refresh")}
          >
            <Refresh />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ py: 0 }}>
          {recentActivities.length > 0 ? (
            recentActivities.map((activity, index) => (
              <React.Fragment key={index}>
                <RecentActivityItem activity={activity} />
                {index < recentActivities.length - 1 && (
                  <Divider component="li" />
                )}
              </React.Fragment>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary={t("admin.recentActivity.noActivity")}
                secondary={t("admin.recentActivity.noActivityDesc")}
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </CustomContainer>
  );
};

export default AdminDashboard;
